import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Experience = () => {
    const { t } = useTranslation();

    const roles = [
        {
            title: t('experience.role1.title'),
            equiv: t('experience.role1.equiv'),
            period: t('experience.role1.period'),
            description: t('experience.role1.desc'),
            tech: ['Kotlin', 'Quarkus', 'Kafka', 'Angular', 'TypeScript', 'Java', 'Node.js', 'GraphQL', 'AEM'],
            current: true,
        },
        {
            title: t('experience.role2.title'),
            equiv: t('experience.role2.equiv'),
            period: t('experience.role2.period'),
            description: t('experience.role2.desc'),
            tech: ['Java', 'Node.js', 'GraphQL', 'AEM', 'JavaScript', 'React'],
        },
        {
            title: t('experience.role3.title'),
            equiv: t('experience.role3.equiv'),
            period: t('experience.role3.period'),
            description: t('experience.role3.desc'),
            via: t('experience.role3.via'),
            tech: ['Java', 'AEM', 'JavaScript'],
        },
    ];

    const certifications = [
        {
            name: t('experience.certs.claudecode.name'),
            issuer: 'Anthropic',
            year: '2026',
            color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400',
            url: 'https://verify.skilljar.com/c/9w3uhgqf6y9v',
        },
        {
            name: t('experience.certs.shopify.name'),
            issuer: 'Shopify',
            year: '2025',
            color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
        },
        {
            name: t('experience.certs.adobe.name'),
            issuer: 'Adobe',
            year: '2023',
            color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
        },
        {
            name: t('experience.certs.ios.name'),
            issuer: 'UniParthenope',
            year: '2020',
            color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
        },
    ];

    return (
        <section id="experience" className="relative section">
            {/* Background orbs for continuity */}
            <div className="absolute inset-0 z-0 pointer-events-none">
                <motion.div 
                    animate={{ x: [0, 50, 0], y: [0, -40, 0] }} 
                    transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute bottom-1/4 -right-20 w-96 h-96 bg-amber-200/20 dark:bg-amber-900/10 rounded-full blur-[120px]"
                />
            </div>

            <div className="container-custom relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    {/* Section Title */}
                    <div className="mb-12">
                        <span className="section-number">02 — Experience</span>
                        <h2 className="section-title mt-2">{t('experience.sectionTitle')}</h2>
                    </div>

                    <div className="space-y-16">
                        {/* Company header */}
                        <motion.div
                            className="flex items-center gap-5 p-6 card bg-white/40 dark:bg-zinc-900/30 border-white/50 dark:border-zinc-800/50 backdrop-blur-xl"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="relative w-16 h-16 shrink-0">
                                <motion.div 
                                    className="absolute -inset-1 rounded-2xl bg-amber-500/20 blur-sm"
                                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                />
                                <div className="relative w-full h-full rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center border border-amber-200/50 dark:border-amber-700/30">
                                    <span className="text-amber-600 dark:text-amber-400 font-bold text-2xl">A</span>
                                </div>
                            </div>
                            <div>
                                <h3 className="font-bold text-xl text-zinc-900 dark:text-zinc-50">Accenture Italia</h3>
                                <p className="text-sm font-mono text-amber-600 dark:text-amber-400">{t('experience.companyPeriod')}</p>
                                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1 italic">{t('experience.levelsNote')}</p>
                            </div>
                        </motion.div>

                        {/* Timeline */}
                        <div className="relative pl-8 md:pl-12 border-l-2 border-dashed border-zinc-200 dark:border-zinc-800 space-y-12 ml-4 md:ml-8">
                            {roles.map((role, index) => (
                                <motion.div
                                    key={index}
                                    className="relative"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    {/* Dot */}
                                    <div className="absolute -left-[41px] md:-left-[57px] top-6 flex items-center justify-center">
                                        <div className={`w-4 h-4 rounded-full border-2 z-10 ${role.current ? 'border-amber-500 bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.5)]' : 'border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950'}`} />
                                        {role.current && (
                                            <motion.div 
                                                className="absolute w-8 h-8 rounded-full bg-amber-500/20"
                                                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            />
                                        )}
                                    </div>

                                    <div className="card p-6 md:p-8 bg-zinc-50/50 dark:bg-zinc-900/20 border-zinc-200/50 dark:border-zinc-800/50">
                                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-3">
                                                    <h4 className="font-bold text-lg md:text-xl text-zinc-900 dark:text-zinc-50">{role.title}</h4>
                                                    {role.current && (
                                                        <span className="px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 font-mono text-[10px] uppercase tracking-wider">
                                                            {t('experience.current')}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-xs text-zinc-400 dark:text-zinc-500">{role.equiv}</span>
                                                    <span className="w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-700" />
                                                    <span className="text-xs text-zinc-500 dark:text-zinc-400">{role.period}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {role.via && (
                                            <p className="text-xs text-zinc-400 dark:text-zinc-500 italic mb-3">{role.via}</p>
                                        )}
                                        <p className="text-zinc-600 dark:text-zinc-400 mb-6 text-sm md:text-base leading-relaxed">{role.description}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {role.tech.map((t, i) => (
                                                <span key={i} className="tag">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* Education and Certifications Stack */}
                    <div className="flex flex-col gap-12 lg:gap-16">
                            {/* Certifications */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="flex items-center gap-4 mb-8">
                                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('experience.certifications')}</h3>
                                    <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                </div>
                                <div className="grid gap-4">
                                    {certifications.map((cert, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ x: 5 }}
                                            className={`card p-5 border-l-4 ${cert.color.split(' ')[0]} dark:border-l-amber-500/50`}
                                        >
                                            <p className="font-bold text-zinc-900 dark:text-zinc-50">{cert.name}</p>
                                            <div className="flex items-center gap-3 mt-1">
                                                <p className="text-xs text-zinc-500 dark:text-zinc-400">{cert.issuer} · {cert.year}</p>
                                                {cert.url && (
                                                    <a
                                                        href={cert.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs font-mono text-violet-600 dark:text-violet-400 hover:underline"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        verify ↗
                                                    </a>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Education */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <div className="flex items-center gap-4 mb-8">
                                    <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-50">{t('experience.education')}</h3>
                                    <div className="h-px flex-1 bg-gradient-to-r from-zinc-200 to-transparent dark:from-zinc-800" />
                                </div>
                                <div className="space-y-4">
                                    <div className="card p-5 hover:bg-white dark:hover:bg-zinc-800/40">
                                        <div className="flex justify-between items-start mb-2">
                                            <p className="font-bold text-zinc-900 dark:text-zinc-50">{t('experience.edu.uni')}</p>
                                            <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500 uppercase">2017 – 2020</span>
                                        </div>
                                        <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('experience.edu.uni_school')}</p>
                                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-2 font-medium">{t('experience.edu.uni_note')}</p>
                                    </div>
                                    <div className="card p-5 hover:bg-white dark:hover:bg-zinc-800/40">
                                        <div className="flex justify-between items-start mb-2">
                                            <p className="font-bold text-zinc-900 dark:text-zinc-50">{t('experience.edu.diploma')}</p>
                                            <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500 uppercase">2012 – 2017</span>
                                        </div>
                                        <p className="text-sm text-zinc-600 dark:text-zinc-400">C.A. Dalla Chiesa, Afragola</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Experience;
